import React, {
  useState,
  useEffect,
  Fragment,
  useMemo,
  type ReactNode,
} from "react";
import "./TemplateBrowser.scss";
import TemplateListItem from "./TemplateListItem/TemplateListItem.js";
import { Tooltip, Loader, TextButton, TextInput } from "@SeemplicityDev/shared";

type TemplateId = string | number;

export interface TemplateOption {
  id: TemplateId;
  name?: string;
  description?: string;
  withHelp?: string;
  // Server template payloads carry extra members the runtime forwards; keep the
  // shape open so callers (TemplateSelect) can share this canonical type.
  [key: string]: unknown;
}

export interface TemplateBrowserProps {
  options?: TemplateOption[];
  defaultTemplate?: TemplateOption;
  onSelectTemplate?: (template?: TemplateOption) => void;
  onSelectTemplateToDisplay?: (templateId: TemplateId) => void;
  onCopyTemplateData?: (template?: TemplateOption) => void;
  isGenericAreaLoading?: boolean;
  children?: ReactNode;
}

const TemplateBrowser: React.FC<TemplateBrowserProps> = ({
  options,
  defaultTemplate,
  onSelectTemplate = () => {},
  onSelectTemplateToDisplay = () => {},
  onCopyTemplateData = () => {},
  isGenericAreaLoading,
  children,
}) => {
  const [displayedTemplateId, setDisplayedTemplateId] = useState<
    TemplateId | undefined
  >();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const displayedMenuItems = useMemo(() => {
    let newOptions = options;

    if (!!searchValue) {
      newOptions = options?.filter((item) =>
        item.name?.toLowerCase()?.includes(searchValue?.toLowerCase())
      );

      if (!newOptions?.length) {
        setDisplayedTemplateId(undefined);
      }
    }

    return newOptions;
  }, [options, searchValue]);

  useEffect(() => {
    if (!!displayedMenuItems?.length && !displayedTemplateId) {
      if (!defaultTemplate) {
        setDisplayedTemplateId(displayedMenuItems[0].id);
      } else {
        setDisplayedTemplateId(defaultTemplate.id);
      }
    }
  }, [displayedMenuItems, defaultTemplate, displayedTemplateId]);

  useEffect(() => {
    if (!!displayedTemplateId) {
      onSelectTemplateToDisplay(displayedTemplateId);
    }
  }, [displayedTemplateId]);

  const onClickTemplateItem = (templateId: TemplateId) => {
    setDisplayedTemplateId(templateId);
  };

  const getTemplateHeader = () => {
    const displayedTemplate = options?.find(
      (template) => template.id === displayedTemplateId
    );

    return (
      <div className={"content-header"}>
        <div className={"content-template-name"}>
          <h4>{displayedTemplate?.name}</h4>
          {displayedTemplate?.withHelp && (
            <Tooltip type="info" content={displayedTemplate.withHelp}>
              <i className={`seem-icon seem-icon-help`} />
            </Tooltip>
          )}
        </div>
        <div className={"template-actions form-button-group"}>
          <TextButton
            data-testid={"copy-template-data"}
            type="button"
            label={"Copy from Template"}
            wrapperClassName={"template-button"}
            disabled={isGenericAreaLoading}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "copy_from_template_btn", "container": "template_browser"}`}
            onClick={() => {
              onCopyTemplateData(displayedTemplate);
            }}
            withTooltip={{
              content:
                "The settings will be applied as-is. You can make changes.",
            }}
          />
          <TextButton
            data-testid={"apply-template"}
            type="button"
            label={"Apply Template"}
            buttonStyle={"primary"}
            disabled={isGenericAreaLoading}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "apply_template_btn", "container": "template_browser"}`}
            onClick={() => {
              onSelectTemplate(displayedTemplate);
            }}
            withTooltip={{
              content:
                "The settings will be applied as-is and locked to the template. You cannot make  changes.",
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={"template-browser-wrapper"}>
      {!options ? (
        <div className="empty-state">
          Create your first template by clicking Save as Template,
          <br />
          below the Add Fields section.
        </div>
      ) : (
        <>
          <div className={"templates-list"}>
            <div className={"templates-list-header"}>
              <TextInput
                data-testid={"search-template"}
                className={"search-template no-padding-bottom"}
                leftIcon={"seem-icon-search"}
                placeholder={"Type to search..."}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchValue(e.target.value || "")
                }
                value={searchValue}
                isClearable
              />
            </div>
            <div className={"templates-list-scrollable"}>
              {displayedMenuItems?.length ? (
                displayedMenuItems.map((template) => (
                  <Fragment key={template.id}>
                    <TemplateListItem
                      id={template.id}
                      // `name`/`searchValue` are optional here but the item
                      // requires `string`; coalesce to "" (same render).
                      title={template.name ?? ""}
                      description={template.description}
                      isSelected={displayedTemplateId === template.id}
                      onClick={onClickTemplateItem}
                      searchValue={searchValue ?? ""}
                    ></TemplateListItem>
                  </Fragment>
                ))
              ) : (
                <div className={"search-empty-state"}>
                  No matching results. Try a different search.
                </div>
              )}
            </div>
          </div>
          {!!displayedTemplateId ? (
            <div className={"template-content"}>
              {getTemplateHeader()}
              <div className={"generic-area"}>{children}</div>
            </div>
          ) : (
            <Loader
              center
              flexcenter
              iconSize={"xxxl"}
              icon={"seem-icon-page"}
              subtitle={`Preview available upon template selection`}
            />
          )}
        </>
      )}
    </div>
  );
};
export default TemplateBrowser;
